import {
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  MathUtils,
  PointLight,
  Scene,
} from "three";

import {
  AUTHORED_INTERIORS,
  INTERIOR_STABILIZATION_CONFIG,
  authoredInteriorWorldPoint,
  type AuthoredInteriorDefinition,
  type AuthoredInteriorId,
} from "../config/interiors.ts";

/**
 * Keep the WebGL light count stable for every interior. Point-light count is a
 * shader-program key in Three.js, so changing it per building causes a cold
 * program compilation at the doorway.
 */
export const INTERIOR_DYNAMIC_POINT_LIGHT_BUDGET = 4;

const INTERIOR_LIGHT_SETTLED_EPSILON = 0.001;

interface InteriorLightRuntimeSpec {
  readonly id: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly color: number;
  readonly intensity: number;
  readonly distanceM: number;
}

export interface InteriorLightingFrameState {
  readonly activeInteriorId: AuthoredInteriorId | null;
  readonly mix01: number;
  readonly exposureScale: number;
}

export interface InteriorLightingController {
  setActiveInterior(id: AuthoredInteriorId | null): void;
  update(
    deltaSeconds: number,
    focus: Readonly<{ x: number; y: number; z: number }>,
  ): InteriorLightingFrameState;
  getState(): InteriorLightingFrameState;
  dispose(): void;
}

function responseAlpha(deltaSeconds: number): number {
  return deltaSeconds <= 0
    ? 0
    : 1 - Math.exp(
        -INTERIOR_STABILIZATION_CONFIG.lightingResponsePerSecond *
          deltaSeconds,
      );
}

function settledMix(previous: number, target: 0 | 1, alpha: number): number {
  const next = MathUtils.lerp(previous, target, alpha);
  if (target === 0 && next <= INTERIOR_LIGHT_SETTLED_EPSILON) {
    return 0;
  }
  if (target === 1 && next >= 1 - INTERIOR_LIGHT_SETTLED_EPSILON) {
    return 1;
  }
  return next;
}

function runtimeLights(
  interior: Readonly<AuthoredInteriorDefinition>,
): readonly InteriorLightRuntimeSpec[] {
  return interior.pointLights.map((spec) => ({
    id: spec.id,
    position: authoredInteriorWorldPoint(interior, spec.positionLocalM),
    color: spec.color,
    intensity: spec.intensity,
    distanceM: spec.distanceM,
  }));
}

function nearestLights(
  lights: readonly InteriorLightRuntimeSpec[],
  focus: Readonly<{ x: number; y: number; z: number }>,
): readonly InteriorLightRuntimeSpec[] {
  return [...lights]
    .sort((left, right) => {
      const leftDistance =
        (left.position.x - focus.x) ** 2 +
        (left.position.y - focus.y) ** 2 +
        (left.position.z - focus.z) ** 2;
      const rightDistance =
        (right.position.x - focus.x) ** 2 +
        (right.position.y - focus.y) ** 2 +
        (right.position.z - focus.z) ** 2;
      return leftDistance - rightDistance || left.id.localeCompare(right.id);
    })
    .slice(0, INTERIOR_DYNAMIC_POINT_LIGHT_BUDGET);
}

export function createInteriorLightingController(
  scene: Scene,
  hemisphere: HemisphereLight,
  direction: DirectionalLight,
  fill: AmbientLight,
): InteriorLightingController {
  const interiorsById = new Map(
    AUTHORED_INTERIORS.map((interior) => [interior.id, interior] as const),
  );
  const lightsByInterior = new Map(
    AUTHORED_INTERIORS.map((interior) => [
      interior.id,
      runtimeLights(interior),
    ] as const),
  );
  const lightPool = Array.from(
    { length: INTERIOR_DYNAMIC_POINT_LIGHT_BUDGET },
    (_, index) => {
      const light = new PointLight(0xffffff, 0, 1, 2);
      light.name = `interior-light-pool:${index + 1}`;
      light.castShadow = false;
      light.visible = false;
      scene.add(light);
      return light;
    },
  );

  let activeInteriorId: AuthoredInteriorId | null = null;
  let transitionInteriorId: AuthoredInteriorId | null = null;
  let mix01 = 0;
  let state: InteriorLightingFrameState = {
    activeInteriorId: null,
    mix01: 0,
    exposureScale: 1,
  };
  let disposed = false;

  function update(
    deltaSeconds: number,
    focus: Readonly<{ x: number; y: number; z: number }>,
  ): InteriorLightingFrameState {
    if (disposed) {
      return state;
    }
    mix01 = settledMix(
      mix01,
      activeInteriorId === null ? 0 : 1,
      responseAlpha(deltaSeconds),
    );
    const transitionInterior = transitionInteriorId === null
      ? null
      : interiorsById.get(transitionInteriorId) ?? null;
    const exteriorScale = transitionInterior === null
      ? 1
      : MathUtils.lerp(1, transitionInterior.exteriorLightScale, mix01);
    const exposureScale = transitionInterior === null
      ? 1
      : MathUtils.lerp(1, transitionInterior.exposureScale, mix01);
    const selectedLights = transitionInteriorId === null
      ? []
      : nearestLights(lightsByInterior.get(transitionInteriorId) ?? [], focus);
    const usesInteriorShaderVariant = transitionInterior !== null && mix01 > 0;

    for (const [index, light] of lightPool.entries()) {
      const selected = selectedLights[index];
      if (selected !== undefined) {
        light.position.set(
          selected.position.x,
          selected.position.y,
          selected.position.z,
        );
        light.color.setHex(selected.color);
        light.distance = selected.distanceM;
        light.intensity = selected.intensity * mix01;
      } else {
        light.position.set(focus.x, focus.y, focus.z);
        light.color.setHex(0xffffff);
        light.distance = 1;
        light.intensity = 0;
      }
      // Unused pool slots intentionally remain visible while inside so every
      // interior uses the same already-warmed four-point-light shader variant.
      light.visible = usesInteriorShaderVariant;
    }

    if (mix01 === 0 && activeInteriorId === null) {
      transitionInteriorId = null;
      for (const light of lightPool) {
        light.intensity = 0;
        light.visible = false;
      }
    }

    // The exterior controller writes its base values immediately before this
    // pass. Multiplication therefore never accumulates across render frames.
    hemisphere.intensity *= exteriorScale;
    direction.intensity *= exteriorScale;
    fill.intensity *= exteriorScale;
    state = {
      activeInteriorId,
      mix01,
      exposureScale,
    };
    return state;
  }

  return {
    setActiveInterior(id): void {
      if (disposed) {
        return;
      }
      if (id !== null && !interiorsById.has(id)) {
        throw new Error(`Unknown authored interior ${id}`);
      }
      activeInteriorId = id;
      if (id !== null) {
        transitionInteriorId = id;
      }
    },
    update,
    getState(): InteriorLightingFrameState {
      return { ...state };
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      for (const light of lightPool) {
        light.removeFromParent();
        light.dispose();
      }
      lightPool.length = 0;
      lightsByInterior.clear();
      interiorsById.clear();
    },
  };
}
